import { api } from '../lib/api'

export type ProSkill = { name: string; slug: string }
export type ProSkillOption = { id: number; name: string; slug: string }
export type ProCompanyOption = { id: number; title: string; slug: string; avatar_url?: string }

export type ProProfile = {
  is_enabled: boolean
  pro_mode: 'REMOTE' | 'LOCAL' | 'BOTH'
  title: string
  bio: string
  category?: string
  subcategory?: string
  visible_in_discovery?: boolean
  hourly_rate_cents?: number | null
  base_rate_cents?: number | null
  currency: string
  service_radius_miles: number
  service_area_text?: string
  available_now: boolean
  pros_to_go_enabled?: boolean
  home_zipcode?: string
  location_lat?: number | null
  location_lng?: number | null
  accept_local_requests?: boolean
  company?: {
    id: number
    slug: string
    title: string
    avatar_url?: string
    cover_url?: string
  } | null
  company_role_title?: string
  company_is_primary?: boolean
  average_rating: number
  rating_count: number
  completed_jobs_count: number
  skills_data: ProSkill[]
}

export type ProActivation = {
  active: boolean
  method: 'SUBSCRIPTION' | 'STAKE_LOCK' | null
  stake_locked_pixe: number
}

export type ProActivationSources = {
  stake_active: boolean
  stake_locked_pixe: string
  subscription_active: boolean
  subscription_plan: string | null
  subscription_checkout_available?: boolean
}

export type ProMeResponse = {
  pro_profile: ProProfile | null
  activation: ProActivation
  activation_sources?: ProActivationSources
  feature_flags?: {
    pros_to_go?: boolean
    pros_to_go_zip_enabled?: boolean
  }
}

export type ProPatchPayload = Partial<{
  title: string
  bio: string
  category: string
  subcategory: string
  visible_in_discovery: boolean
  hourly_rate_cents: number | null
  base_rate_cents: number | null
  currency: string
  service_radius_miles: number
  service_area_text: string
  available_now: boolean
  pro_mode: 'REMOTE' | 'LOCAL' | 'BOTH'
  skills: string[]
  pros_to_go_enabled: boolean
  home_zipcode: string
  location_lat: number | null
  location_lng: number | null
  accept_local_requests: boolean
  company_page_id: number | null
  company_role_title: string
}>

export type ProProfileCompleteness = {
  is_discoverable: boolean
  missing: string[]
}

export async function getProMe(): Promise<ProMeResponse> {
  const { data } = await api.get<ProMeResponse>('/api/pros/me/')
  return data
}

export async function patchProMe(payload: ProPatchPayload): Promise<ProMeResponse> {
  const { data } = await api.patch<ProMeResponse>('/api/pros/me/', payload)
  return data
}

export async function getProProfileCompleteness(): Promise<ProProfileCompleteness> {
  const { data } = await api.get<ProProfileCompleteness>('/api/pros/profile/completeness/')
  return data
}

export async function enablePro(): Promise<ProMeResponse> {
  const { data } = await api.post<ProMeResponse>('/api/pros/me/enable/', {})
  return data
}

export async function disablePro(): Promise<ProMeResponse> {
  const { data } = await api.post<ProMeResponse>('/api/pros/me/disable/', {})
  return data
}

export async function stakeActivatePro(amount_pixe = 200): Promise<ProMeResponse> {
  const { data } = await api.post<ProMeResponse>('/api/pros/me/stake_activate/', { amount_pixe })
  return data
}

export async function subscribeActivatePro(): Promise<ProMeResponse> {
  const { data } = await api.post<ProMeResponse>('/api/pros/me/subscribe_activate/', {})
  return data
}

export async function startProsSubscriptionCheckout(): Promise<{ url: string; id?: string }> {
  const { data } = await api.post<{ url: string; id?: string }>('/api/billing/subscribe/pros/', {})
  return data
}

export async function devStakeLock(stake_locked_pixe?: number): Promise<{ ok: boolean }> {
  const payload = stake_locked_pixe ? { stake_locked_pixe } : {}
  const { data } = await api.post<{ ok: boolean }>('/api/pros/dev/stake-lock/', payload)
  return data
}

export type RequestProPayload = {
  title?: string
  summary: string
  details_md?: string
  client_location_text: string
  client_zipcode: string
  urgency?: 'FLEXIBLE' | 'TODAY' | 'ASAP'
  scheduled_for?: string | null
  attachments?: unknown[]
}

export async function requestPro(userId: number | string, payload: RequestProPayload): Promise<{ job_id: number; post_id: number; url: string }> {
  const { data } = await api.post<{ job_id: number; post_id: number; url: string }>(`/api/pros/${userId}/request/`, payload)
  return data
}

export type InviteProPayload = {
  title?: string
  summary: string
  details_md?: string
  compensation_mode?: 'HOURLY' | 'FIXED'
  proposed_fixed_price_pixe?: number | null
  proposed_hourly_pixe?: number | null
}

export async function inviteProToJob(userId: number | string, payload: InviteProPayload): Promise<{ job_id: number; post_id: number; thread_id?: number; url: string }> {
  const { data } = await api.post<{ job_id: number; post_id: number; thread_id?: number; url: string }>(`/api/pros/${userId}/invite_job/`, payload)
  return data
}

export async function searchProSkills(q: string, limit = 20): Promise<ProSkillOption[]> {
  const { data } = await api.get<ProSkillOption[]>('/api/pros/skills/', {
    params: { q, limit },
  })
  return data
}

export async function listProCompanyPages(q = '', limit = 20): Promise<ProCompanyOption[]> {
  const { data } = await api.get<ProCompanyOption[]>('/api/pros/company_pages/', {
    params: { q, limit },
  })
  return Array.isArray(data) ? data : []
}
